/**
 * Utility functions for transforming variable references between storage format (internal IDs)
 * and display format (step names with labels)
 *
 * Multi-level path support:
 * - Storage: {{uuid-123.Order.orderId}} or {{uuid-123.Order.shippingAddress.city}}
 * - Display: {{Load Order.Order.Order ID}} or {{Load Order.Order.Shipping Address.City}}
 *
 * The first segment is always the step reference ID (UUID).
 * Subsequent segments are resolved using the type registry to get labels.
 */

import {
	VARIABLE_TYPE_REGISTRY,
	type VariableTypeKey,
} from '@supply-mover-2-0/shared';

export interface NodeInfo {
	id: string; // Reference ID (UUID) - persistent across saves
	stepName: string; // Display name for UI
}

/**
 * Resolve a field key to its label using the type registry.
 * If no type context is provided, returns the key as-is.
 */
export function resolveFieldLabel(
	fieldKey: string,
	typeKey?: VariableTypeKey,
): string {
	if (typeKey == null) {
		return fieldKey;
	}

	const typeDef = VARIABLE_TYPE_REGISTRY[typeKey];

	if (typeDef == null) {
		return fieldKey;
	}

	const propertyDef = typeDef.properties[fieldKey];

	if (propertyDef == null) {
		return fieldKey;
	}

	return propertyDef.label;
}

/**
 * Get the type key for a nested property within a type.
 * Returns undefined if the property is primitive or not found.
 */
export function getNestedTypeKey(
	fieldKey: string,
	parentTypeKey?: VariableTypeKey,
): VariableTypeKey | undefined {
	if (parentTypeKey == null) {
		return undefined;
	}

	const typeDef = VARIABLE_TYPE_REGISTRY[parentTypeKey];

	if (typeDef == null) {
		return undefined;
	}

	const propertyDef = typeDef.properties[fieldKey];

	if (propertyDef == null) {
		return undefined;
	}

	return propertyDef.arrayType ?? propertyDef.objectType;
}

/**
 * Parse a variable path into segments.
 * Example: "uuid-123.Order.shippingAddress.city" -> ["uuid-123", "Order", "shippingAddress", "city"]
 */
export function parseVariablePath(path: string): Array<string> {
	return path.split('.');
}

/**
 * Transform a variable reference from display format to storage format.
 * Converts step names to UUIDs and labels back to field keys.
 *
 * Example: "{{Load Order.Order.Order ID}}" -> "{{uuid-123.Order.orderId}}"
 */
export function transformVariableToStorage(
	value: string,
	nodeMap: Map<string, NodeInfo>,
	labelToKeyMap?: Map<string, string>,
): string {
	if (value == null || !value.includes('{{')) {
		return value;
	}

	// Match any mustache variable - we'll parse the path ourselves
	const pattern = /\{\{([^}]+)\}\}/g;

	return value.replace(pattern, (match, fullPath: string) => {
		const segments = parseVariablePath(fullPath);

		if (segments.length === 0) {
			return match;
		}

		const stepNameOrId = segments[0];

		if (stepNameOrId == null) {
			return match;
		}

		// Find the node with this step name
		const node = Array.from(nodeMap.values()).find(
			(n) => n.stepName === stepNameOrId,
		);

		if (node != null) {
			// Replace first segment with UUID
			segments[0] = node.id;

			// If we have a label-to-key map, convert labels back to keys
			if (labelToKeyMap != null) {
				for (let i = 1; i < segments.length; i++) {
					const segment = segments[i];

					if (segment != null) {
						const key = labelToKeyMap.get(segment);

						if (key != null) {
							segments[i] = key;
						}
					}
				}
			}

			return `{{${segments.join('.')}}}`;
		}

		// If no matching node found, keep original (might be a UUID already)
		return match;
	});
}

/**
 * Transform a variable reference from storage format to display format.
 * Converts UUIDs to step names and field keys to labels.
 *
 * Example: "{{uuid-123.Order.orderId}}" -> "{{Load Order.Order.Order ID}}"
 */
export function transformVariableToDisplay(
	value: string,
	nodeMap: Map<string, NodeInfo>,
	rootTypeKey?: VariableTypeKey,
): string {
	if (value == null || !value.includes('{{')) {
		return value;
	}

	// Match any mustache variable
	const pattern = /\{\{([^}]+)\}\}/g;

	return value.replace(pattern, (match, fullPath: string) => {
		const segments = parseVariablePath(fullPath);

		if (segments.length === 0) {
			return match;
		}

		const referenceId = segments[0];

		if (referenceId == null) {
			return match;
		}

		// Get the node info by reference ID (UUID)
		const node = nodeMap.get(referenceId);

		if (node != null) {
			// Replace first segment with step name
			const displaySegments: Array<string> = [node.stepName];

			// Convert remaining segments to labels using type registry
			let currentTypeKey = rootTypeKey;

			for (let i = 1; i < segments.length; i++) {
				const segment = segments[i];

				if (segment == null) {
					continue;
				}

				// Try to get the label for this field
				const label = resolveFieldLabel(segment, currentTypeKey);
				displaySegments.push(label);

				// Update the type context for nested resolution
				currentTypeKey = getNestedTypeKey(segment, currentTypeKey);
			}

			return `{{${displaySegments.join('.')}}}`;
		}

		// If no matching node found, it might already be a step name, keep original
		return match;
	});
}

/**
 * Transform all variable references in a config object from display to storage format.
 */
export function transformConfigToStorage(
	config: unknown,
	nodeMap: Map<string, NodeInfo>,
	labelToKeyMap?: Map<string, string>,
): unknown {
	if (config == null) {
		return config;
	}

	const configStr = JSON.stringify(config);
	const transformedStr = transformVariableToStorage(
		configStr,
		nodeMap,
		labelToKeyMap,
	);

	return JSON.parse(transformedStr);
}

/**
 * Transform all variable references in a config object from storage to display format.
 */
export function transformConfigToDisplay(
	config: unknown,
	nodeMap: Map<string, NodeInfo>,
	rootTypeKey?: VariableTypeKey,
): unknown {
	if (config == null) {
		return config;
	}

	const configStr = JSON.stringify(config);
	const transformedStr = transformVariableToDisplay(
		configStr,
		nodeMap,
		rootTypeKey,
	);

	return JSON.parse(transformedStr);
}

/**
 * Validate that all variable references in a value point to valid upstream nodes.
 */
export function validateVariableReferences(
	value: string,
	upstreamNodeIds: Set<string>,
): { valid: boolean; invalidReferences: Array<string> } {
	if (value == null || !value.includes('{{')) {
		return { valid: true, invalidReferences: [] };
	}

	const invalidReferences: Array<string> = [];
	const pattern = /\{\{([^}]+)\}\}/g;
	let match;

	while ((match = pattern.exec(value)) !== null) {
		const fullPath = match[1];

		if (fullPath == null) {
			continue;
		}

		const segments = parseVariablePath(fullPath);
		const nodeIdOrName = segments[0];

		// Skip system variables and Start variables
		if (nodeIdOrName === 'Start' || segments.length < 2) {
			continue;
		}

		// Check if this node ID is in the upstream nodes
		if (nodeIdOrName != null && !upstreamNodeIds.has(nodeIdOrName)) {
			invalidReferences.push(match[0]);
		}
	}

	return {
		valid: invalidReferences.length === 0,
		invalidReferences,
	};
}

/**
 * Get all variable references from a value.
 */
export function extractVariableReferences(value: string): Array<string> {
	if (value == null || !value.includes('{{')) {
		return [];
	}

	const references: Array<string> = [];
	const pattern = /\{\{([^}]+)\}\}/g;
	let match;

	while ((match = pattern.exec(value)) !== null) {
		if (match[1] != null) {
			references.push(match[1]);
		}
	}

	return references;
}

/**
 * Build a path with labels from a path with keys.
 * Used for display in the UI.
 *
 * Example: ["Order", "shippingAddress", "city"] with ORDER type
 *       -> ["Order", "Shipping Address", "City"]
 */
export function buildLabelPath(
	keyPath: Array<string>,
	rootTypeKey?: VariableTypeKey,
): Array<string> {
	const labelPath: Array<string> = [];
	let currentTypeKey = rootTypeKey;

	for (const key of keyPath) {
		const label = resolveFieldLabel(key, currentTypeKey);
		labelPath.push(label);
		currentTypeKey = getNestedTypeKey(key, currentTypeKey);
	}

	return labelPath;
}

/**
 * Build a full variable path with labels for display.
 * Includes step name as the first segment.
 *
 * Example: ("Load Order", ["Order", "shippingAddress", "city"], ORDER)
 *       -> "Load Order > Order > Shipping Address > City"
 */
export function buildDisplayPath(
	stepName: string,
	keyPath: Array<string>,
	rootTypeKey?: VariableTypeKey,
	separator = ' > ',
): string {
	const labelPath = buildLabelPath(keyPath, rootTypeKey);

	return [stepName, ...labelPath].join(separator);
}
